import { Key } from '@solid-primitives/keyed'
import { FaSolidArrowRight, FaSolidRotate, FaSolidX } from 'solid-icons/fa'
import { Component, createMemo, createSignal, on, Show, useContext, type Accessor } from 'solid-js'
import { modifyMutable, produce } from 'solid-js/store'
import calculateDraftMoveAttackEstimates from '../../lib/core/draft_move/calculateDraftMoveAttackEstimates'
import createDraftMove from '../../lib/core/draft_move/createDraftMove'
import type { DraftMove } from '../../lib/core/draft_move/DraftMove.type'
import type { Engine } from '../../lib/core/engine/Engine.type'
import resetEngineToDefaultSelectedTool from '../../lib/core/engine/resetEngineToDefaultSelectedTool'
import type { AnnexableEntityUnion } from '../../lib/core/entity/AnnexableEntityUnion.type'
import canUnitAnnexEntity from '../../lib/core/entity/canUnitAnnexEntity'
import entityDisplayName from '../../lib/core/entity/entityDisplayName'
import { isEntityInfantry } from '../../lib/core/entity/entityTypeMetaList.generated'
import getEntitiesAtPosition from '../../lib/core/entity/getEntitiesAtPosition'
import type { Entity } from '../../lib/core/entity/index'
import unitHasRangedAttackWeapon from '../../lib/core/entity/unitHasRangedAttackWeapon'
import unitHasUsableRangedAttackWeapon from '../../lib/core/entity/unitHasUsableRangedAttackWeapon'
import type { HasHP } from '../../lib/core/has_hp'
import type { HasWeapons } from '../../lib/core/HasWeapons'
import { SelectedToolId } from '../../lib/core/map_editor/SelectedToolId.enum'
import type { HasPlayerId } from '../../lib/core/player/has_player_id'
import { createMoveUnitAction } from '../../lib/core/state/flux/action/Game/MoveUnitAction'
import dispatchClient from '../../lib/core/state/flux/dispatchClient'
import type { PathTurnSteps_Step } from '../../lib/core/tile_position_xy/findMostEfficientPathToTileV2/PathTurnSteps_Step.type'
import findUnloadableCargoTileCollection, { type UnloadableCargoTileGroup } from '../../lib/core/tile_position_xy/findUnloadableCargoTileCollection'
import { samePosition } from '../../lib/core/tile_position_xy/samePosition'
import toCoord from '../../lib/core/tile_position_xy/toCoord'
import handlePreviewRangedAttackOptions, { shouldOfferPreviewRangedAttackOptions } from '../../lib/core/ui/action/PreviewRangedAttackOptions'
import handlePreviewUnloadPositionOptions from '../../lib/core/ui/action/PreviewUnloadPositionOptions'
import handleUnitLoadIntoEntityAction from '../../lib/core/ui/action/UnitLoadIntoEntityAction'
import { EventResult } from '../../lib/event/event'
import { InputActionType } from '../../lib/event/InputRouter/InputActionType'
import KeyboardShortcut from '../../lib/event/InputRouter/KeyboardShortcut.component'
import { mountInputActionListener } from '../../lib/event/InputRouter/mountInputActionListener'
import Button from '../../lib/jsx/Button'
import nullIfEmptyArray from '../../lib/ldsh/nullIfEmptyArray'
import onNextTick from '../../lib/ldsh/onNextTick'
import withBusy from '../../lib/withBusy'
import { useAuthPlayerIdSignal } from '../../rx/signal/useAuthPlayerIdSignal'
import { useSelectedToolSignal } from '../../rx/signal/useSelectedToolSignal'
import type { Nullable } from '../../typescript'
import EngineContext from '../EngineContext'
import LoadingIcon from '../LoadingIcon'
import SingleEntityIcon3D from '../MapEditor/SingleEntityIcon3D'

const DraftMoveMenuWidget: Component = () => {
  const engine: Engine = useContext(EngineContext)
  const [getAuthPlayerId] = useAuthPlayerIdSignal(engine)

  const yourTurn = (): boolean => {
    const authPlayerId = getAuthPlayerId()
    return (authPlayerId && authPlayerId === engine.state.turnPlayerId) as boolean
  }
  const [, setSelectedTool] = useSelectedToolSignal(engine)

  return (
    <Show when={yourTurn() && !engine.state.ended && engine.draftMove} children={(draftMove) => {

      const lastStep = (): Nullable<PathTurnSteps_Step> => draftMove().destPositionPaths?.lastStep

      const [isBusy, setIsBusy] = createSignal<boolean>(false)

      const annexableEntityList = createMemo<Nullable<Array<AnnexableEntityUnion>>>(() => {
        const { destPosition, destPositionPaths, unit } = draftMove()
        if (destPosition) {
          return destPositionPaths?.lastStep?.annex
        }
        if (isEntityInfantry(unit)) {
          return getEntitiesAtPosition(engine.state.ents, unit).filter((ent) => canUnitAnnexEntity(unit, ent))
        }
      })

      const directAttackableEntityList = createMemo<Array<Entity & HasHP & HasPlayerId>>(() => {
        const list: Array<Entity & HasHP & HasPlayerId> = []
        lastStep()?.enemies?.forEach?.((row) => {
          if (row.canDirectAttack) {
            list.push(row.ent as Entity & HasHP & HasPlayerId)
          }
        })
        return list
      })
      // createEffect(() => {
      //   console.log('directAttackableEntityList', deepClone(directAttackableEntityList()))
      // })

      const unitWithRangedAttackWeapon = createMemo<Nullable<Entity & HasWeapons>>(() => {
        const { unit } = draftMove()
        if (unitHasRangedAttackWeapon(unit)) {
          return unit
        }
      })
      const unitWithUsableRangedAttackWeapon = createMemo<Nullable<Entity & HasWeapons>>(() => {
        const unit = unitWithRangedAttackWeapon()
        if (unit && unitHasUsableRangedAttackWeapon(unit)) {
          return unit
        }
      })

      // const rangedAttackableEntityList = createMemo<Array<Entity & HasHP & HasPlayerId>>(() => {
      //   const list : Array<Entity & HasHP & HasPlayerId> = []
      //   lastStep()?.enemies?.forEach?.((row) => {
      //     if (row.canRangedAttack) {
      //       list.push(row.ent as Entity & HasHP & HasPlayerId)
      //     }
      //   })
      //   return list
      // })
      // createEffect(() => {
      //   console.log('rangedAttackableEntityList', deepClone(rangedAttackableEntityList()))
      // })
      // createEffect(() => {
      //   console.log('[DraftMoveMenuWidget].effect.lastStep', deepClone(lastStep()))
      // })

      const unloadableCargoPathGroupList = createMemo<Nullable<Array<UnloadableCargoTileGroup>>>(on(() => engine.draftMove, (draftMove: Nullable<DraftMove>) => {
        return nullIfEmptyArray(findUnloadableCargoTileCollection(engine, draftMove))
      }))
      const showRangedAttackButton = createMemo(() => {
        return shouldOfferPreviewRangedAttackOptions(engine)
      })

      // eslint-disable-next-line solid/reactivity
      const doCancel = withBusy(isBusy, setIsBusy, async () => {
        modifyMutable(engine, produce((engine) => {
          resetEngineToDefaultSelectedTool(engine)
        }))
      })

      mountInputActionListener(InputActionType.Escape, () => {
        doCancel()
        return EventResult.Stop
      })

      // const cssStyle = createMemo<Partial<JSX.CSSProperties>>(() => {
      //   return {
      //     // position: 'fixed',
      //     // left: '10px',
      //     // top: '10px',
      //   }
      // })
      return (
        <div class="cw cw-dm cw-sm-bl float-end">
          <Key each={directAttackableEntityList()} by="id"
            children={(directAttackableEntity) => {
              return <Button
                ariaLabel={`Attack ${entityDisplayName(directAttackableEntity())}`}
                disabled={isBusy()}
                name="direct_attack"
                class="btn btn-primary d-block"
                ref={(elem: HTMLButtonElement) => {
                  // onmouseenter/onmouseleave does not include children
                  // onmouseover/onmouseout includes children
                  elem.onmouseover = () => {
                    const targetEnt = directAttackableEntity()
                    const position = toCoord(targetEnt)
                    modifyMutable(engine, produce((engine) => {
                      const { draftMove } = engine
                      if (draftMove && !samePosition(draftMove.attackPosition, position)) {
                        draftMove.attackPosition = position
                        calculateDraftMoveAttackEstimates(engine)
                      }
                    }))
                  }
                  elem.onmouseout = () => {
                    const targetEnt = directAttackableEntity()
                    const position = toCoord(targetEnt)
                    modifyMutable(engine, produce((engine) => {
                      const { draftMove } = engine
                      if (draftMove && samePosition(draftMove.attackPosition, position)) {
                        draftMove.attackPosition = null
                        calculateDraftMoveAttackEstimates(engine)
                      }
                    }))
                  }
                }}
                onClick={withBusy(isBusy, setIsBusy, async () => {
                  const targetEnt = directAttackableEntity()
                  const newDraftMove: DraftMove = {
                    ...draftMove(),
                    attackPosition: toCoord(targetEnt),
                    target: targetEnt,
                  }
                  setSelectedTool(SelectedToolId.MoveUnit)
                  engine.draftMove = newDraftMove
                  calculateDraftMoveAttackEstimates(engine)
                  await dispatchClient(engine, createMoveUnitAction(newDraftMove))
                  modifyMutable(engine, produce((engine) => {
                    resetEngineToDefaultSelectedTool(engine)
                  }))
                })}
              >
                <Show when={isBusy()} children={<LoadingIcon />} fallback={<FaSolidArrowRight />} />
                {` Attack ${entityDisplayName(directAttackableEntity())} `}
                <Show when={directAttackableEntityList().length === 1}
                  children={<span class="text-muted small">[d]</span>}
                />
              </Button>
            }}
          />
          <Show when={showRangedAttackButton()}
            children={(
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              rangedUnit) => {
              return <Button
                ariaLabel={`See bombard attack options`}
                disabled={isBusy() || !unitWithUsableRangedAttackWeapon()}
                name="ranged_attack"
                class="btn btn-primary d-block"
                onClick={withBusy(isBusy, setIsBusy, async () => {
                  handlePreviewRangedAttackOptions(engine)
                })}
              >
                <Show when={isBusy()} children={<LoadingIcon />} fallback={<FaSolidArrowRight />} />
                {` Bombard `}
                <span class="text-muted small">[b]</span>
              </Button>
            }}
          />
          <Key each={annexableEntityList()} by="id" children={(annexableEntity) => {
            // const annexableEntityTypeMeta = () => 
            return <Button
              ariaLabel={`Annex ${annexableEntity()}`}
              disabled={isBusy()}
              name="annex"
              class="btn btn-primary d-block"
              onClick={withBusy(isBusy, setIsBusy, async () => {
                const newDraftMove: DraftMove = {
                  ...draftMove(),
                  annex: true,
                }
                setSelectedTool(SelectedToolId.MoveUnit)
                engine.draftMove = newDraftMove
                calculateDraftMoveAttackEstimates(engine)
                await dispatchClient(engine, createMoveUnitAction(newDraftMove))
                modifyMutable(engine, produce((engine) => {
                  resetEngineToDefaultSelectedTool(engine)
                }))
              })}
            >
              <Show when={isBusy()} children={<LoadingIcon />} fallback={<FaSolidArrowRight />} />
              {' Annex '}
              <SingleEntityIcon3D entity={annexableEntity()} size={16} />
              {` ${entityDisplayName(annexableEntity())}`}
              <span class="text-muted small">[a]</span>
            </Button>
          }}
          />
          <Show when={lastStep()?.merge} children={(mergableEntity) => {
            // const mergableEntityTypeMeta = () => 
            return <Button
              ariaLabel={`Merge selected unit into ${mergableEntity()}`}
              disabled={isBusy()}
              name="merge"
              class="btn btn-primary d-block"
              onClick={withBusy(isBusy, setIsBusy, async () => {
                await dispatchClient(engine, createMoveUnitAction({
                  ...draftMove(),
                  merge: mergableEntity(),
                }))
                modifyMutable(engine, produce((engine) => {
                  resetEngineToDefaultSelectedTool(engine)
                }))
              })}
            >
              <Show when={isBusy()} children={<LoadingIcon />} fallback={<FaSolidArrowRight />} />
              {` Merge ${entityDisplayName(mergableEntity())} `}
              <span class="text-muted small">[m]</span>
            </Button>
          }}
          />
          <Key each={lastStep()?.pickup} by="id" children={(potentialCargoEnt: Accessor<Entity>) => {
            return (
              <Button
                ariaLabel={`Pickup ${potentialCargoEnt()}`}
                disabled={isBusy()}
                name="pickup"
                class="btn btn-primary d-block"
                onClick={withBusy(isBusy, setIsBusy, async () => {
                  await dispatchClient(engine, createMoveUnitAction({
                    ...draftMove(),
                    pickup: potentialCargoEnt(),
                  }))
                  modifyMutable(engine, produce((engine) => {
                    resetEngineToDefaultSelectedTool(engine)
                  }))
                })}
              >
                <Show when={isBusy()} children={<LoadingIcon />} fallback={<FaSolidArrowRight />} />
                {' Pickup '}
                <span class="text-muted small">[p]</span>
              </Button>
            )
          }} />
          <Key each={lastStep()?.taxis} by="id" children={(
            taxiEnt: Accessor<Entity>
          ) => {
            return (
              <Button
                ariaLabel={`Load Into ${taxiEnt()}`}
                disabled={isBusy()}
                name="pickup"
                class="btn btn-primary d-block"
                onClick={withBusy(isBusy, setIsBusy, async () => {
                  await handleUnitLoadIntoEntityAction(engine)
                })}
              >
                <Show when={isBusy()} children={<LoadingIcon />} fallback={<FaSolidArrowRight />} />
                {' Load '}
                <span class="text-muted small">[L]</span>
              </Button>
            )
          }} />
          <Show when={unloadableCargoPathGroupList()} children={(unloadableCargoPathGroupList: Accessor<Array<UnloadableCargoTileGroup>>) => {
            return <Key each={unloadableCargoPathGroupList()} by="cargo_id" children={(ucPathGroup: Accessor<UnloadableCargoTileGroup>) => {
              return <Button
                ariaLabel={`See Unload Options`}
                disabled={isBusy()}
                name="unload"
                class="btn btn-primary d-block"
                classList={{
                  'btn-secondary': draftMove()?.unload?.cargo_id === ucPathGroup().cargo_id
                }}
                onClick={() => {
                  handlePreviewUnloadPositionOptions(engine)
                }}
              >
                <Show when={isBusy()} children={<LoadingIcon />} fallback={<FaSolidArrowRight />} />
                {' Unload '}
                <span class="text-muted small">[u]</span>
              </Button>
            }} />
          }} />
          <Show when={lastStep()?.occupant} children={(occupant: Accessor<Entity>) => {
            return <Button
              ariaLabel={`Cancel Current Move and Select ${entityDisplayName(occupant())} Unit`}
              name="occupant"
              class="btn btn-secondary d-block"
              onClick={withBusy(isBusy, setIsBusy, async () => {
                const newSelectedEnt = occupant()
                modifyMutable(engine, produce((engine) => {
                  resetEngineToDefaultSelectedTool(engine)
                }))
                await onNextTick(() => {
                  modifyMutable(engine, produce((engine) => {
                    setSelectedTool(SelectedToolId.MoveUnit)
                    engine.draftMove = newSelectedEnt ? createDraftMove(newSelectedEnt) : null
                    calculateDraftMoveAttackEstimates(engine)
                  }))
                })
              })}
            >
              <FaSolidRotate />
              {' Select '}
              <SingleEntityIcon3D entity={occupant()} size={24} />
            </Button>
          }} />
          <Show when={draftMove().destPosition && !(lastStep()?.occupant)}>
            <Button
              ariaLabel={`Commit current movement.`}
              disabled={isBusy()}
              name="commit"
              class="btn btn-primary d-block"
              onClick={withBusy(isBusy, setIsBusy, async () => {
                await dispatchClient(engine, createMoveUnitAction(draftMove()))
                modifyMutable(engine, produce((engine) => {
                  resetEngineToDefaultSelectedTool(engine)
                }))
              })}
            >
              <Show when={isBusy()} children={<LoadingIcon />} fallback={<FaSolidArrowRight />} />
              {' Commit '}
              <span class="text-muted small">[c]</span>
            </Button>
          </Show>
          <Show when={draftMove() && !draftMove().destPosition}>
            <Button
              ariaLabel={`Commit current movement and have your ${entityDisplayName(draftMove().unit)} wait until next turn.`}
              disabled={isBusy()}
              name="wait"
              class="btn btn-primary d-block"
              onClick={withBusy(isBusy, setIsBusy, async () => {
                await dispatchClient(engine, createMoveUnitAction({
                  ...draftMove(),
                  wait: true,
                }))
                modifyMutable(engine, produce((engine) => {
                  resetEngineToDefaultSelectedTool(engine)
                }))
              })}
            >
              <Show when={isBusy()} children={<LoadingIcon />} fallback={<FaSolidX />} />
              {' Wait '}
              <span class="text-muted small">[w]</span>
            </Button>
          </Show>
          <Show when={draftMove()}>
            <Button
              ariaLabel={`Cancel uncommited move.`}
              disabled={isBusy()}
              name="cancel"
              class="btn btn-primary d-block"
              onClick={doCancel}
            >
              <Show when={isBusy()} children={<LoadingIcon />} fallback={<FaSolidX />} />
              {' Cancel '}<KeyboardShortcut action={InputActionType.Escape} />
            </Button>
          </Show>
        </div>
      )
    }} />
  )
}

export default DraftMoveMenuWidget
